import { Style, Avatar } from '@dicebear/core';
import glyphsJson from '@dicebear/styles/glyphs.json' with { type: 'json' };
import cameoJson from '@dicebear/styles/cameo.json' with { type: 'json' };
import loreleiJson from '@dicebear/styles/lorelei.json' with { type: 'json' };
import botttsJson from '@dicebear/styles/bottts.json' with { type: 'json' };
import adventurerJson from '@dicebear/styles/adventurer.json' with { type: 'json' };
import initialsJson from '@dicebear/styles/initials.json' with { type: 'json' };

export type SupportedDiceBearStyle =
  | 'glyphs'
  | 'cameo'
  | 'lorelei'
  | 'bottts'
  | 'adventurer'
  | 'initials';

const styleDefinitions: Record<SupportedDiceBearStyle, any> = {
  glyphs: glyphsJson,
  cameo: cameoJson,
  lorelei: loreleiJson,
  bottts: botttsJson,
  adventurer: adventurerJson,
  initials: initialsJson,
};

const parsedStyleCache = new Map<SupportedDiceBearStyle, Style>();

function getParsedStyle(styleName: SupportedDiceBearStyle = 'glyphs'): Style {
  let cached = parsedStyleCache.get(styleName);
  if (!cached) {
    const rawDef = styleDefinitions[styleName] ?? glyphsJson;
    cached = new Style(rawDef);
    parsedStyleCache.set(styleName, cached);
  }
  return cached;
}

export interface ResolveAvatarSeedParams {
  userId?: string | null;
  phone?: string | null;
  email?: string | null;
}

/**
 * Resolves avatar seed according to fallback order:
 * userId ?? phone ?? email ?? 'user'
 */
export function resolveAvatarSeed(params?: ResolveAvatarSeedParams | null): string {
  if (!params) return 'user';
  const userId = params.userId?.trim();
  if (userId) return userId;
  const phone = params.phone?.trim();
  if (phone) return phone;
  const email = params.email?.trim();
  if (email) return email;
  return 'user';
}

export interface DiceBearOptions {
  style?: SupportedDiceBearStyle;
  size?: number;
  [key: string]: any;
}

/**
 * Generates a Data URI for a DiceBear avatar using Glyphs as default style.
 */
export function getDiceBearAvatarUri(seed: string, options?: DiceBearOptions): string {
  const { style: styleName = 'glyphs', size = 128, ...extraOptions } = options ?? {};
  const style = getParsedStyle(styleName);
  const avatar = new Avatar(style, {
    seed: seed || 'user',
    size,
    ...extraOptions,
  });

  return avatar.toDataUri();
}

/**
 * Generates raw SVG markup for a DiceBear avatar.
 */
export function getDiceBearAvatarSvg(seed: string, options?: DiceBearOptions): string {
  const { style: styleName = 'glyphs', size = 128, ...extraOptions } = options ?? {};
  const style = getParsedStyle(styleName);
  const avatar = new Avatar(style, {
    seed: seed || 'user',
    size,
    ...extraOptions,
  });

  return avatar.toString();
}
